import {
  OnShowStepCallback,
  OnShowStepUnsubscriber,
  StepInfo,
  StepState,
  StepWizardState,
} from './types';
import { generateId } from '../../utils';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Alert } from '@ssar-app/uikit';

const StepWizardContext = createContext<StepWizardState | undefined>(undefined);

export const useStepWizardContext = (): StepWizardState => {
  const context = useContext(StepWizardContext);
  if (!context) {
    throw Error(`Could not find StepWizardContext in the component hierarchy.`);
  }
  return context;
};
export const StepWizardContextProvider = StepWizardContext.Provider;

const StepContext = createContext<StepState | undefined>(undefined);

export const useStepContext = (): StepState => {
  const context = useContext(StepContext);
  if (!context) {
    throw Error(`Could not find StepContext in the component hierarchy.`);
  }

  return context;
};
const StepContextProvider = StepContext.Provider;

type StepWrapperProps = {
  stepKey: string;
  children: ReactNode;
};

type StepTransitionHandlers = {
  onActivate?: () => Promise<boolean>;
  onDeactivate?: () => Promise<boolean>;
};

const PromieTrueFn = () => Promise.resolve(true);

const StepWrapper = (props: StepWrapperProps) => {
  const { children, stepKey } = props;
  const {
    wasStepActive: wasStepActiveFn,
    activeStep,
    onShowStep,
  } = useStepWizardContext();
  const stepTransitionHandlersRef = useRef<
    Record<string, StepTransitionHandlers>
  >({});

  const onStepActivate = useCallback(
    (callback: StepTransitionHandlers['onActivate']) => {
      if (!stepTransitionHandlersRef.current[stepKey]) {
        stepTransitionHandlersRef.current[stepKey] = {};
      }
      stepTransitionHandlersRef.current[stepKey].onActivate = callback;
    },
    [stepKey],
  );

  const onStepDeactivate = useCallback(
    (callback: StepTransitionHandlers['onDeactivate']) => {
      if (!stepTransitionHandlersRef.current[stepKey]) {
        stepTransitionHandlersRef.current[stepKey] = {};
      }
      stepTransitionHandlersRef.current[stepKey].onDeactivate = callback;
    },
    [stepKey],
  );

  useEffect(() => {
    const unsubscribe = onShowStep(
      async (nextStep: string, prevStep?: string) => {
        const onDeactivate = prevStep
          ? stepTransitionHandlersRef.current[prevStep]?.onDeactivate ||
            PromieTrueFn
          : PromieTrueFn;
        const onActivate =
          stepTransitionHandlersRef.current[nextStep]?.onActivate ||
          PromieTrueFn;
        const prevResult = await onDeactivate();
        if (prevResult) {
          const nextResult = await onActivate();
          return nextResult;
        }
        return prevResult;
      },
    );
    return unsubscribe;
  }, [onShowStep]);

  const isStepActive = activeStep === stepKey;
  const wasStepActive = wasStepActiveFn(stepKey);

  const value: StepState = useMemo(() => {
    return {
      isStepActive,
      wasStepActive,
      onStepActivate,
      onStepDeactivate,
    };
  }, [isStepActive, onStepActivate, onStepDeactivate, wasStepActive]);

  return <StepContextProvider value={value}>{children}</StepContextProvider>;
};

const convertToKeysMaps = (steps: StepInfo[]) => {
  return steps.reduce(
    (acc, curr, currentIndex, source) => {
      const key = curr.key;
      if (!key) {
        throw new Error(
          `Wizard steps prop is having steps with key/stepKey unassigned. stepIndex: ${currentIndex}`,
        );
      }
      acc[key] = {
        previousStep: source[currentIndex - 1]?.key,
        index: currentIndex,
        nextStep: source[currentIndex + 1]?.key,
      };
      return acc;
    },
    {} as Record<
      string,
      {
        previousStep?: string;
        index: number;
        nextStep?: string;
      }
    >,
  );
};

const ErrorComponent = (props: { error: unknown }) => {
  let message = '';
  let description = '';
  const { error } = props;
  if (error instanceof Error) {
    message = `${error.name}`;
    description = `${error.message}, ${
      error.stack ? 'Stack: ' + error.stack : ''
    }`;
  } else {
    message = 'Error';
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    description = (error as any)?.toString() || 'Unknown error';
  }

  return (
    <Alert
      title={message}
      description={description}
      type='error'
      isOpen
      showCloseButton={false}
      showIcon
    />
  );
};

export type StepWizardProps = {
  activeStep?: string;
  steps: StepInfo[];
  onError?: (error: unknown) => ReactNode;
  children?: (props: StepWizardRenderProps) => JSX.Element;
};

export type StepWizardRenderProps = {
  wizardState: StepWizardState;
  steps: JSX.Element[];
};

export const StepWizard = (props: StepWizardProps) => {
  const {
    steps,
    children,
    activeStep: propsActiveStep,
    onError = (e) => <ErrorComponent error={e}></ErrorComponent>,
  } = props;
  const stepKeysMap = useMemo(() => convertToKeysMaps(steps), [steps]);
  const [error, setError] = useState<unknown>();

  const [firstStepKey, lastStepKey] = useMemo(() => {
    const keys = Object.keys(stepKeysMap);
    return [
      keys.find((a) => stepKeysMap[a].index === 0),
      keys.find((a) => stepKeysMap[a].index === steps.length - 1),
    ];
  }, [stepKeysMap, steps.length]);

  const activeStepFromProps = useMemo(() => {
    const key = propsActiveStep;
    if (key && (stepKeysMap[key] === undefined || stepKeysMap[key] === null)) {
      console.warn(
        `Wizard doesn't contain step with the key/stepKey with value ${propsActiveStep}. Defaulting activeStep to ${firstStepKey}`,
      );
      return null;
    }
    return key;
  }, [firstStepKey, propsActiveStep, stepKeysMap]);

  const [activeStep, setActiveStepState] = useState(activeStepFromProps);

  const totalSteps = steps.length;

  const visitedStepsRef = useRef<string[]>([]);
  const onShowStepSubscriptions = useRef<Map<string, OnShowStepCallback>>(
    new Map(),
  );

  const onShowStep: (callback: OnShowStepCallback) => OnShowStepUnsubscriber =
    useCallback((callback: OnShowStepCallback) => {
      const subscriptionKey = generateId();
      onShowStepSubscriptions.current.set(subscriptionKey, callback);
      return () => {
        onShowStepSubscriptions.current.delete(subscriptionKey);
      };
    }, []);

  const [areStepsLoading, setAreStepsLoading] = useState(true);

  const setActiveStep = useCallback(
    (stepKey: string, shouldThrowOnStepNotFound = false) => {
      const stepInfo = stepKeysMap[stepKey];
      if (shouldThrowOnStepNotFound && (!stepKey || !stepInfo)) {
        throw new Error(`Step with stepKey: ${stepKey} was not found`);
      }

      const subscribedShowStepFunctionsPromiseFn = (
        nextStep: string,
        prevStep?: string | null,
      ) =>
        Array.from(onShowStepSubscriptions.current.values()).reduce(
          async (acc, curr) => {
            const accResult = await acc;
            if (accResult) {
              return curr(nextStep, prevStep || undefined);
            }
            return await Promise.resolve(accResult);
          },
          Promise.resolve(true),
        );

      setActiveStepState((currentStepKey) => {
        setAreStepsLoading(true);

        const showStepFn = async () => {
          try {
            const shouldShowStep = await subscribedShowStepFunctionsPromiseFn(
              stepKey,
              currentStepKey,
            );
            if (shouldShowStep) {
              if (
                currentStepKey &&
                currentStepKey !== stepKey &&
                !visitedStepsRef.current.includes(currentStepKey)
              ) {
                visitedStepsRef.current.push(currentStepKey);
              }
              setActiveStepState(stepKey);
            }
          } catch (error) {
            console.error(error);
            setError(error);
          } finally {
            setAreStepsLoading(false);
          }
        };
        showStepFn();

        return currentStepKey;
      });
    },
    [stepKeysMap],
  );

  useEffect(() => {
    if (activeStepFromProps) {
      setActiveStep(activeStepFromProps, true);
    } else if (!activeStep) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      setActiveStep(firstStepKey!);
    }
  }, [activeStep, activeStepFromProps, firstStepKey, setActiveStep]);

  const providerValue: StepWizardState = useMemo(() => {
    return {
      areStepsLoading,
      nextStep() {
        if (activeStep) {
          setActiveStep(
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            stepKeysMap[activeStep]?.nextStep || lastStepKey!,
            activeStep,
          );
        }
      },
      previousStep() {
        if (activeStep) {
          setActiveStep(
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            stepKeysMap[activeStep]?.previousStep || firstStepKey!,
            activeStep,
          );
        }
      },
      goToStep(stepKey: string) {
        setActiveStep(stepKey, true);
      },
      isFirstStep: activeStep === firstStepKey,
      isLastStep: activeStep === lastStepKey,
      activeStep: activeStep || null,
      totalSteps,
      onShowStep,
      wasStepActive(stepKey: string) {
        return visitedStepsRef.current.includes(stepKey);
      },
    };
  }, [
    activeStep,
    firstStepKey,
    lastStepKey,
    onShowStep,
    setActiveStep,
    stepKeysMap,
    totalSteps,
    areStepsLoading,
  ]);

  const wrappedSteps = steps.map((a) => {
    return (
      <StepWrapper stepKey={a.key} key={a.key}>
        {a.step.element}
      </StepWrapper>
    );
  });
  const childrenNode = useMemo(() => {
    return children
      ? children({
          wizardState: providerValue,
          steps: wrappedSteps,
        })
      : null;
  }, [children, providerValue, wrappedSteps]);
  return (
    <StepWizardContextProvider value={providerValue}>
      {!!error && onError(error)}
      {!error && childrenNode}
    </StepWizardContextProvider>
  );
};

// StepWizard.whyDidYouRender = false;
